import type { OrganizationBrandAssetType } from "@/repository/organizationBrandingRepository";

export const ORGANIZATION_BRAND_IMAGE_SPECS = {
  icon: {
    width: 512,
    height: 512,
    safePadding: 0.12,
    fileName: "organization-icon.webp",
  },
  "horizontal-logo": {
    width: 1200,
    height: 400,
    safePadding: 0.08,
    fileName: "organization-horizontal-logo.webp",
  },
} as const;

export type OrganizationBrandImageAdjustment = {
  zoom: number;
  offsetX: number;
  offsetY: number;
};

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("画像を読み込めませんでした"));
    };
    image.src = url;
  });

export async function normalizeOrganizationBrandImage(
  file: File,
  assetType: OrganizationBrandAssetType,
  adjustment: OrganizationBrandImageAdjustment
): Promise<File> {
  const spec = ORGANIZATION_BRAND_IMAGE_SPECS[assetType];
  const image = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = spec.width;
  canvas.height = spec.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("画像編集を開始できませんでした");

  const innerWidth = spec.width * (1 - spec.safePadding * 2);
  const innerHeight = spec.height * (1 - spec.safePadding * 2);
  const baseScale = Math.min(
    innerWidth / image.naturalWidth,
    innerHeight / image.naturalHeight
  );
  const scale = baseScale * adjustment.zoom;
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  const offsetLimitX = spec.width * 0.1;
  const offsetLimitY = spec.height * 0.1;
  const x = (spec.width - drawWidth) / 2 + adjustment.offsetX * offsetLimitX;
  const y = (spec.height - drawHeight) / 2 + adjustment.offsetY * offsetLimitY;
  context.clearRect(0, 0, spec.width, spec.height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, x, y, drawWidth, drawHeight);

  let blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/webp", 0.92)
  );
  let contentType = "image/webp";
  let fileName: string = spec.fileName;
  if (!blob) {
    blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );
    contentType = "image/png";
    fileName = spec.fileName.replace(/\.webp$/, ".png");
  }
  if (!blob) throw new Error("画像を変換できませんでした");
  return new File([blob], fileName, { type: contentType });
}
